import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Conversation, Message } from "@twilio/conversations";

import { Button } from "@twilio-paste/button";
import { Box, Input, Text, Spinner, Tooltip } from "@twilio-paste/core";
import { SearchIcon } from "@twilio-paste/icons/esm/SearchIcon";

import {
  SideModal,
  SideModalBody,
  SideModalContainer,
  SideModalFooter,
  SideModalHeader,
  SideModalHeading,
  useSideModalState,
} from "@twilio-paste/core/side-modal";

type Props = {
  sdkConvo?: Conversation;
  disabled?: boolean;
  onSelectMessage?: (msg: Message) => void;
  maxToLoad?: number;
};

type LoadState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready" }
  | { status: "error"; message: string };

export const ConversationSearchDrawer: React.FC<Props> = ({
  sdkConvo,
  disabled = false,
  onSelectMessage,
  maxToLoad = 300,
}) => {
  const sideModal = useSideModalState({ placement: "right" });

  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadState, setLoadState] = useState<LoadState>({ status: "idle" });

  const didLoadRef = useRef(false);
  const convoSidRef = useRef<string | undefined>(undefined);

  // Reset quando cambia conversazione
  useEffect(() => {
    const sid = sdkConvo?.sid;
    const changed = convoSidRef.current && sid && convoSidRef.current !== sid;

    convoSidRef.current = sid;

    if (changed) {
      didLoadRef.current = false;
      setMessages([]);
      setQuery("");
      setLoadState({ status: "idle" });
    }
  }, [sdkConvo?.sid]);

  useEffect(() => {
    if (!sideModal.visible) setQuery("");
  }, [sideModal.visible]);

  // Carica messaggi quando apro il drawer (una volta per conversazione)
  useEffect(() => {
    const run = async () => {
      if (!sdkConvo) return;
      if (!sideModal.visible) return;
      if (didLoadRef.current) return;

      didLoadRef.current = true;
      setLoadState({ status: "loading" });

      try {
        const pageSize = Math.min(50, maxToLoad);
        const all: Message[] = [];

        let paginator: any = await (sdkConvo as any).getMessages(pageSize);

        while (paginator?.items?.length) {
          all.push(...paginator.items);
          if (all.length >= maxToLoad) break;
          if (!paginator.hasNextPage) break;
          paginator = await paginator.nextPage();
        }

        const bySid = new Map<string, Message>();
        for (const m of all) bySid.set(m.sid, m);

        const unique = Array.from(bySid.values()).sort((a, b) => {
          const ta = a.dateCreated ? a.dateCreated.getTime() : 0;
          const tb = b.dateCreated ? b.dateCreated.getTime() : 0;
          return ta - tb;
        });

        setMessages(unique);
        setLoadState({ status: "ready" });
      } catch (e: any) {
        didLoadRef.current = false;
        setLoadState({
          status: "error",
          message: e?.message ?? "Errore durante il caricamento dei messaggi",
        });
      }
    };

    void run();
  }, [sdkConvo, sideModal.visible, maxToLoad]);

  const normalizedQuery = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (!normalizedQuery) return messages;

    return messages.filter((m) => {
      const body = (m.body ?? "").toLowerCase();
      const author = (m.author ?? "").toLowerCase();
      return body.includes(normalizedQuery) || author.includes(normalizedQuery);
    });
  }, [messages, normalizedQuery]);

  const handlePick = (m: Message) => {
    onSelectMessage?.(m);
  };

  const isButtonDisabled = disabled || !sdkConvo;

  return (
    <>
      <Tooltip text="Ricerca">
        <Box
          as="button"
          type="button"
          aria-label="Ricerca"
          onClick={() => sideModal.show()}
          disabled={isButtonDisabled}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          padding="space20"
          borderRadius="borderRadius20"
          borderStyle="none"
          backgroundColor="transparent"
          cursor={isButtonDisabled ? "not-allowed" : "pointer"}
          _focus={{
            outline: "none",
            boxShadow: "none",
          }}
          _focusVisible={{
            outline: "none",
            boxShadow: "none",
          }}
          _disabled={{
            opacity: "0.5",
          }}
        >
          <SearchIcon decorative={false} title="Ricerca" />
        </Box>
      </Tooltip>

      <SideModalContainer state={sideModal}>
        <SideModal aria-label="Ricerca messaggi" {...sideModal}>
          <SideModalHeader>
            <SideModalHeading>Ricerca messaggi</SideModalHeading>
          </SideModalHeader>

          <SideModalBody>
            <Box>
              <Input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca in questa conversazione…"
                insertBefore={<SearchIcon decorative size="sizeIcon20" />}
              />

              <Box marginTop="space40">
                {loadState.status === "loading" ? (
                  <Box display="flex" alignItems="center" columnGap="space30">
                    <Spinner
                      decorative={false}
                      title="Loading"
                      size="sizeIcon30"
                    />
                    <Text as="span" color="colorTextWeak">
                      Caricamento messaggi…
                    </Text>
                  </Box>
                ) : loadState.status === "error" ? (
                  <Box>
                    <Text as="p" color="colorTextError">
                      {loadState.message}
                    </Text>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => {
                        didLoadRef.current = false;
                        setLoadState({ status: "idle" });
                      }}
                    >
                      Riprova
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Box marginBottom="space30">
                      <Text as="p" color="colorTextWeak">
                        {normalizedQuery
                          ? `${results.length} risultati`
                          : `Messaggi caricati: ${messages.length}`}
                      </Text>
                    </Box>

                    <Box
                      borderStyle="solid"
                      borderWidth="borderWidth10"
                      borderColor="colorBorderWeak"
                      borderRadius="borderRadius20"
                      overflowY="auto"
                    >
                      {results.length === 0 ? (
                        <Box padding="space50">
                          <Text as="p" color="colorTextWeak">
                            Nessun messaggio trovato.
                          </Text>
                        </Box>
                      ) : (
                        results.map((m) => (
                          <Box
                            key={m.sid}
                            padding="space40"
                            borderBottomStyle="solid"
                            borderBottomWidth="borderWidth10"
                            borderBottomColor="colorBorderWeak"
                            _hover={{
                              backgroundColor: "colorBackgroundSubaccount",
                            }}
                            cursor="pointer"
                            role="button"
                            tabIndex={0}
                            onClick={() => handlePick(m)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handlePick(m);
                              }
                            }}
                          >
                            <Box display="flex" justifyContent="space-between">
                              <Text
                                as="span"
                                fontSize="fontSize20"
                                color="colorTextWeak"
                              >
                                {m.author ?? "—"}
                              </Text>
                              <Text
                                as="span"
                                fontSize="fontSize20"
                                color="colorTextWeak"
                              >
                                {m.dateCreated
                                  ? m.dateCreated.toLocaleString()
                                  : ""}
                              </Text>
                            </Box>

                            <Box marginTop="space20">
                              <Text
                                as="p"
                                style={{
                                  margin: 0,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {m.body ?? ""}
                              </Text>
                            </Box>
                          </Box>
                        ))
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </SideModalBody>

          <SideModalFooter>
            <Button
              variant="secondary"
              fullWidth
              onClick={() => {
                setQuery("");
                sideModal.hide();
              }}
            >
              Chiudi
            </Button>
          </SideModalFooter>
        </SideModal>
      </SideModalContainer>
    </>
  );
};
